<template>
  <div class="min-h-screen bg-background text-foreground flex flex-col md:flex-row p-4 md:p-6 lg:p-8 gap-6 md:gap-10 max-w-[1100px] mx-auto md:items-start">
    
    <!-- Static Anchor Profile -->
    <aside class="w-full md:w-[280px] lg:w-[320px] md:sticky md:top-8 flex flex-col gap-2 md:h-[calc(100vh-64px)] shrink-0">
      <div class="flex justify-between items-center">
        <!-- Dynamic Logo -->
        <div
          class="logo-shell"
          :class="{ 'logo-shell--ready': logosReady }"
          aria-label="JG Logo"
          role="img"
        >
          <div v-if="!logosReady" class="logo-placeholder" aria-hidden="true"></div>
          <img
            src="/icons/whitethemelogo.svg"
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            class="theme-logo"
            :class="{ 'theme-logo--visible': isDark && logosReady }"
          />
          <img
            src="/icons/blackthemelogo.svg"
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            class="theme-logo"
            :class="{ 'theme-logo--visible': !isDark && logosReady }"
          />
        </div>
        
        <!-- Segmented Theme Toggle -->
        <div class="flex items-center bg-muted p-0.5">
          <button 
            @click="setTheme('light')" 
            :class="['h-7 w-8 flex items-center justify-center transition-all', !isDark ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground']"
            aria-label="Light Mode"
          >
            <font-awesome-icon icon="sun" class="h-3.5 w-3.5" />
          </button>
          <button 
            @click="setTheme('dark')" 
            :class="['h-7 w-8 flex items-center justify-center transition-all', isDark ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground']"
            aria-label="Dark Mode"
          >
            <font-awesome-icon icon="moon" class="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div>
          <h1 class="text-2xl lg:text-3xl font-bold tracking-tight mb-1">Jayson Gomba</h1>
          <h2 class="text-sm lg:text-base text-muted-foreground font-medium">Developer</h2>
        </div>

        <p class="text-[0.85rem] text-muted-foreground leading-relaxed">
          <font-awesome-icon icon="map-marker-alt" class="mr-1"/> PHL
        </p>

        <div class="flex flex-col gap-2 mt-2">
          <Button as="a" href="mailto:jysngomba.rbm@gmail.com" class="w-full justify-start gap-3 h-9 text-xs">
            <font-awesome-icon icon="envelope" /> Contact Me
          </Button>
          <div class="flex gap-2">
            <Button as="a" href="https://github.com/jayror0" target="_blank" rel="noopener noreferrer" variant="outline" class="w-full h-9">
              <font-awesome-icon :icon="['fab', 'github']" class="h-4 w-4" />
            </Button>
            <Button as="a" href="https://www.linkedin.com/in/jaysongomba" target="_blank" rel="noopener noreferrer" variant="outline" class="w-full h-9">
              <font-awesome-icon :icon="['fab', 'linkedin']" class="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <!-- Desktop Contact Section -->
      <ContactSection class="hidden md:flex md:mt-auto pt-6 border-t border-border/50" />
    </aside>

    <!-- Interactive Segmented Area -->
    <main class="w-full md:flex-1 min-w-0">
      <SegmentedTabs v-model="activeTab" :tabs="tabsList" class="overflow-x-auto pb-1 hide-scrollbar">
        
        <div v-show="activeTab === 'about'" class="mt-4 lg:mt-6 animation-fade">
          <div class="flex flex-col gap-4">
            <div class="flex items-center justify-between border-b pb-3">
              <h2 class="text-xl font-semibold tracking-tight">About</h2>
            </div>
            <div class="text-sm text-muted-foreground text-justify leading-loose space-y-4 max-w-2xl py-2">
              <p>
                A developer focused on scalable, accessible, and high-performance digital products, with work that spans the full web stack from backend architecture to refined front-end implementation.
              </p>
              <p>
                Strong grounding in practical UI/UX informs an approach centered on clean systems, accessible interactions, and minimal interfaces that feel organized, responsive, and easy to use.
              </p>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'projects'" class="mt-4 lg:mt-6 animation-fade">
          <ProjectsSection />
        </div>
        
        <div v-show="activeTab === 'experience'" class="mt-4 lg:mt-6 animation-fade">
          <ExperienceSection />
        </div>
        
        <div v-show="activeTab === 'skills'" class="mt-4 lg:mt-6 animation-fade">
          <SkillsSection />
        </div>
        
        <div v-show="activeTab === 'certificates'" class="mt-4 lg:mt-6 animation-fade">
          <CertificatesSection />
        </div>

      </SegmentedTabs>

      <!-- Mobile Page Footer -->
      <footer class="w-full mt-16 pt-8 border-t border-border/50 md:hidden">
        <ContactSection />
      </footer>
    </main>

  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import Button from '../components/ui/Button.vue';
import SegmentedTabs from '../components/ui/SegmentedTabs.vue';

import ProjectsSection from '../components/ProjectsSection.vue';
import ExperienceSection from '../components/ExperienceSection.vue';
import SkillsSection from '../components/SkillsSection.vue';
import CertificatesSection from '../components/CertificatesSection.vue';
import ContactSection from '../components/ContactSection.vue';

export default {
  name: "HomePage",
  components: {
    Button,
    SegmentedTabs,
    ProjectsSection,
    ExperienceSection,
    SkillsSection,
    CertificatesSection,
    ContactSection
  },
  setup() {
    const isDark = ref(false);
    const logosReady = ref(false);
    let themeObserver = null;

    const preloadImage = (src) =>
      new Promise((resolve) => {
        const image = new Image();
        image.src = src;

        const finish = () => resolve();

        image.onload = () => {
          if (typeof image.decode === 'function') {
            image.decode().catch(() => {}).finally(finish);
            return;
          }

          finish();
        };

        image.onerror = finish;

        if (image.complete) {
          if (typeof image.decode === 'function') {
            image.decode().catch(() => {}).finally(finish);
            return;
          }

          finish();
        }
      });

    onMounted(() => {
      isDark.value = document.documentElement.classList.contains('dark');

      Promise.all([
        preloadImage('/icons/whitethemelogo.svg'),
        preloadImage('/icons/blackthemelogo.svg')
      ]).finally(() => {
        logosReady.value = true;
      });

      themeObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            isDark.value = document.documentElement.classList.contains('dark');
          }
        });
      });
      themeObserver.observe(document.documentElement, { attributes: true });
    });

    onBeforeUnmount(() => {
      if (themeObserver) {
        themeObserver.disconnect();
      }
    });

    const setTheme = (theme) => {
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
        localStorage.theme = 'dark';
      } else {
        document.documentElement.classList.remove('dark');
        localStorage.theme = 'light';
      }
    };

    const activeTab = ref('about');
    const tabsList = [
      { label: 'About', value: 'about' },
      { label: 'Recent Projects', value: 'projects' },
      { label: 'Experience', value: 'experience' },
      { label: 'Tech Stack', value: 'skills' },
      { label: 'Certificates', value: 'certificates' }
    ];

    return {
      isDark,
      logosReady,
      setTheme,
      activeTab,
      tabsList
    };
  }
};
</script>

<style scoped>
.logo-shell {
  position: relative;
  width: 12rem;
  height: 4rem;
  flex-shrink: 0;
}

.logo-placeholder,
.theme-logo {
  position: absolute;
  width: 100%;
  height: 100%;
}

.logo-placeholder {
  top: 50%;
  left: 0;
  width: 4rem;
  height: 3.5rem;
  transform: translateY(-50%);
  border-radius: 0.235rem;
  background: linear-gradient(
    90deg,
    rgba(127, 127, 127, 0.14) 0%,
    rgba(127, 127, 127, 0.26) 50%,
    rgba(127, 127, 127, 0.14) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
}

.theme-logo {
  inset: 0;
  object-fit: contain;
  object-position: left center;
  opacity: 0;
  transition: opacity 0.50s ease;
  will-change: opacity;
}

.theme-logo--visible {
  opacity: 1;
}

.logo-shell--ready .logo-placeholder {
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.animation-fade {
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(2px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

@media (max-width: 768px) {
  .logo-shell {
    width: 10rem;
    height: 4rem;
  }
}
</style>
